/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { ICalloutContentStyleProps, ICalloutContentStyles } from '@fluentui/react/lib/Callout';
import { IDropdownOption, IDropdownStyleProps, IDropdownStyles } from '@fluentui/react/lib/Dropdown';
import { ILabelProps, ILabelStyles, Label } from '@fluentui/react/lib/Label';
import { Stack, StackItem } from '@fluentui/react/lib/Stack';
import { Toggle } from '@fluentui/react/lib/Toggle';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import { observer } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { BasicModal, BasicModalProps } from 'src/core/components/BasicModal';
import { CalloutButton } from 'src/core/components/CalloutButton';
import { ExternalLink } from 'src/core/components/ExternalLink';
import { LuisDropdown } from 'src/core/components/LuisDropdown';
import { LuisText } from 'src/core/components/LuisText';
import { LuisTextField } from 'src/core/components/LuisTextField';
import { LuisTooltip } from 'src/core/components/LuisTooltip';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useProgressTracker } from 'src/core/hooks/useProgressTracker';
import { AzureCsAccountKind, AzureSku } from 'src/core/stores/AzureCsAccountStore';
import { AzureResourceGroupStore } from 'src/core/stores/AzureResourceGroupStore';
import { AzureSubscriptionStore } from 'src/core/stores/AzureSubscriptionStore';
import { Region } from 'src/core/types/regionTypes';
import { FormValidators } from 'src/core/utils/formValidators';
import { globalMessages } from 'src/core/utils/globalMessages';
import { azureResourceModalsMessages, getModalText } from 'src/modules/azure/components/azureResourceModalsMessages';

export type AzureCsAccountCreationModalProps = BasicModalProps<{ resourceName: string; isManagedIdentityEnabled: boolean }> & {
    skus: AzureSku[];
    regions: Region[];
    selectedRegion: Region;
    areSkusLoading: boolean;
    areRegionsLoading: boolean;
    selectedSubscriptionId: string;
    selectedResourceGroupId: string;
    areSubscriptionsLoading: boolean;
    selectedSkuCode: AzureSku['code'];
    areResourceGroupsLoading: boolean;
    serviceTarget: AzureCsAccountKind;
    subscriptions: AzureSubscriptionStore[];
    resourcesGroups: AzureResourceGroupStore[];
    onChangeRegion: (newRegion: Region, hasError: boolean) => void;
    onChangeSku: (newSkuCode: AzureSku['code'], hasError: boolean) => void;
    onChangeSubscription: (newSubscriptionId: string, hasError: boolean) => void;
    onChangeResourceGroup: (newResourceGroupId: string, hasError: boolean) => void;
    regionsValidators?: ((newRegion: Region) => ReactIntl.FormattedMessage.MessageDescriptor)[];
    skuValidators?: ((skuCode: AzureSku['code']) => ReactIntl.FormattedMessage.MessageDescriptor)[];
    resourceNameValidators?: ((newResourceName: string) => ReactIntl.FormattedMessage.MessageDescriptor)[];
    subscriptionsValidators?: ((newSubscriptionId: string) => ReactIntl.FormattedMessage.MessageDescriptor)[];
    resourceGroupsValidators?: ((newResourceGroupId: string) => ReactIntl.FormattedMessage.MessageDescriptor)[];
};

const messages = defineMessages({
    createResourceGroup: { id: 'keys.azureCsAccountCreationModal.createResourceGroup', defaultMessage: `Create new '{resourceGroupName}'` },
    invalidResourceGroupCreation: {
        id: 'keys.azureCsAccountCreationModal.invalidResourceGroupCreation',
        defaultMessage: 'Can`t create resource group without selecting a location first'
    },
    resourceGroupDescription: {
        id: 'keys.azureCsAccountCreationModal.resourceGroupDescription',
        defaultMessage: 'A resource group is a collection of resources that share the same lifecycle, permissions, and policies.'
    },
    pricingTierDescription: {
        id: 'keys.azureCsAccountCreationModal.pricingTierDescription',
        defaultMessage: `The resource offers different pricing tiers to fit your needs.
          The pricing tier you select determines how much you will be billed each month.`
    }
});

const dropdownStyles = classNamesFunction<IDropdownStyleProps, IDropdownStyles>()({ root: { maxWidth: '80%' } });
const flexLabelStyles = classNamesFunction<ILabelProps, ILabelStyles>()({ root: { display: 'flex', alignItems: 'flex-end' } });
const dropdownCalloutStyles = classNamesFunction<ICalloutContentStyleProps, ICalloutContentStyles>()({
    calloutMain: { maxHeight: '300px !important' }
});

const getDescribedLabel = (label: string, description: React.ReactNode, isRequired: boolean = false) => (
    <Label styles={flexLabelStyles} required={isRequired}>
        {label}
        <CalloutButton content={description} />
    </Label>
);

export const AzureCsAccountCreationModal = observer((props: AzureCsAccountCreationModalProps) => {
    const {
        skus,
        regions,
        onSubmit,
        onChangeSku,
        serviceTarget,
        subscriptions,
        areSkusLoading,
        selectedRegion,
        onChangeRegion,
        resourcesGroups,
        selectedSkuCode,
        areRegionsLoading,
        skuValidators = [],
        onChangeSubscription,
        onChangeResourceGroup,
        selectedSubscriptionId,
        areSubscriptionsLoading,
        selectedResourceGroupId,
        areResourceGroupsLoading,
        subscriptionsValidators = [id => FormValidators.createRequiredValidator()(id)?.messageDescriptor],
        resourceGroupsValidators = [id => FormValidators.createRequiredValidator()(id)?.messageDescriptor],
        regionsValidators = [region => FormValidators.createRequiredValidator()(region)?.messageDescriptor],
        resourceNameValidators = [
            name => FormValidators.createRequiredValidator()(name)?.messageDescriptor,
            name =>
                FormValidators.createBlacklistValidator({
                    characterList: '\\/:?&=*+()%$~!><%_',
                    errorMessage: azureResourceModalsMessages.invalidName
                })(name)?.messageDescriptor
        ]
    } = props;
    const { localize } = useLocalization();
    const { dispatcher, rootStore } = useGlobalState();
    const { modalTitle, modalInstructions, learnMoreMessage, learnMoreLink } = getModalText(serviceTarget, 'create');
    const resourceGroupCreationTracker = useProgressTracker<AzureResourceGroupStore>({
        onComplete: resourceGroup => onResourceGroupChange({ key: resourceGroup.id, text: resourceGroup.name })
    });

    const [newResourceName, setNewResourceName] = React.useState('');
    const [skuErrorMessage, setSkuErrorMessage] = React.useState('');
    const [regionsErrorMessage, setRegionsErrorMessage] = React.useState('');
    const [resourceNameErrorMessage, setResourceNameErrorMessage] = React.useState('');
    const [subscriptionsErrorMessage, setSubscriptionsErrorMessage] = React.useState('');
    const [resourceGroupErrorMessage, setResourceGroupErrorMessage] = React.useState('');
    const [isManagedIdentityEnabled, setIsManagedIdentityEnabled] = React.useState(false);

    const createResourceGroupItemKey = 'createResourceGroup';
    const currentResource = rootStore.azureStore.session.csAccountStore;
    const selectedRegionToUse = selectedRegion ?? currentResource?.region;
    const invalidResourceGroupCreation = !Boolean(selectedRegionToUse);
    const isModalValid =
        selectedRegion &&
        selectedSkuCode &&
        newResourceName &&
        !skuErrorMessage &&
        selectedSubscriptionId &&
        selectedResourceGroupId &&
        !resourceNameErrorMessage &&
        !subscriptionsErrorMessage &&
        !resourceGroupErrorMessage;

    const onRenderResourceGroupOption = (rGOptionProps: IDropdownOption, defaultRenderer: (props?: IDropdownOption) => JSX.Element) => (
        <LuisTooltip
            content={localize(messages.invalidResourceGroupCreation)}
            hidden={rGOptionProps.key !== createResourceGroupItemKey || !invalidResourceGroupCreation}>
            {defaultRenderer(rGOptionProps)}
        </LuisTooltip>
    );

    const validateField = (
        value: string,
        validators: ((newValue: string) => ReactIntl.FormattedMessage.MessageDescriptor)[],
        errorMessage: string,
        errorMessageSetter: React.Dispatch<React.SetStateAction<string>>
    ) => {
        const errors: ReactIntl.FormattedMessage.MessageDescriptor[] = validators
            .reduce((acc, v) => [...acc, v(value)], [])
            .filter(Boolean);
        const hasError = Boolean(errors.length);

        if (hasError) {
            errorMessageSetter(localize(errors[0]));
        } else if (errorMessage) {
            errorMessageSetter('');
        }

        return hasError;
    };

    const onSubscriptionChange = (newSubscriptionId: string) =>
        onChangeSubscription(
            newSubscriptionId,
            validateField(newSubscriptionId, subscriptionsValidators, subscriptionsErrorMessage, setSubscriptionsErrorMessage)
        );

    const onResourceGroupChange = (resourceGroupOption: IDropdownOption) => {
        if (resourceGroupOption.key === createResourceGroupItemKey) {
            createResourceGroup(resourceGroupOption.data as string);

            return;
        }

        onChangeResourceGroup(
            resourceGroupOption.key as string,
            validateField(
                resourceGroupOption.key as string,
                resourceGroupsValidators,
                resourceGroupErrorMessage,
                setResourceGroupErrorMessage
            )
        );
    };

    const onResourceNameChange = (resourceName: string) => {
        validateField(resourceName, resourceNameValidators, resourceNameErrorMessage, setResourceNameErrorMessage);

        setNewResourceName(resourceName);
    };

    const onSkuChange = (newSkuCode: AzureSku['code']) =>
        onChangeSku(newSkuCode, validateField(newSkuCode, skuValidators, skuErrorMessage, setSkuErrorMessage));

    const onRegionChange = (newRegion: Region) =>
        onChangeRegion(newRegion, validateField(newRegion, regionsValidators, regionsErrorMessage, setRegionsErrorMessage));

    // Creates a new resource group in the same selected region if exists
    // else the same region as the currently selected resource.
    const createResourceGroup = (resourceGroupName: string) => {
        dispatcher.dispatch('createAzureResourceGroup', {
            resourceGroupName,
            id: selectedSubscriptionId,
            location: selectedRegionToUse,
            tracker: resourceGroupCreationTracker
        });
    };

    return (
        <BasicModal
            {...props}
            valid={isModalValid}
            disabled={resourceGroupCreationTracker.isRunning || props.disabled}
            errorMessage={props.errorMessage || resourceGroupCreationTracker.error?.message}
            onSubmit={() => onSubmit({ resourceName: newResourceName, isManagedIdentityEnabled })}
            dialogContentProps={{ title: modalTitle }}>
            <Stack tokens={{ childrenGap: 15 }}>
                <LuisText>
                    {modalInstructions}&nbsp;
                    <ExternalLink href={learnMoreLink}>{learnMoreMessage}</ExternalLink>
                </LuisText>

                <LuisDropdown
                    required
                    styles={dropdownStyles}
                    isLoading={areSubscriptionsLoading}
                    selectedKey={selectedSubscriptionId}
                    errorMessage={subscriptionsErrorMessage}
                    data-automation-id="subscriptionsDropdown"
                    calloutProps={{ styles: dropdownCalloutStyles }}
                    onChange={(_, opt) => onSubscriptionChange(opt.key as string)}
                    label={localize(azureResourceModalsMessages.subscriptionLabel)}
                    options={subscriptions.map(item => ({ key: item.id, text: item.name }))}
                    placeholder={localize(azureResourceModalsMessages.subscriptionPlaceholder)}
                />

                <LuisDropdown
                    styles={dropdownStyles}
                    disabled={!selectedSubscriptionId}
                    isLoading={areResourceGroupsLoading}
                    selectedKey={selectedResourceGroupId}
                    errorMessage={resourceGroupErrorMessage}
                    data-automation-id="resourceGroupsDropdown"
                    onRenderOption={onRenderResourceGroupOption}
                    calloutProps={{ styles: dropdownCalloutStyles }}
                    onChange={(_, opt) => onResourceGroupChange(opt)}
                    options={resourcesGroups.map(rG => ({ key: rG.id, text: rG.name }))}
                    placeholder={localize(azureResourceModalsMessages.resourceGroupPlaceholder)}
                    ariaLabel={`${localize(azureResourceModalsMessages.resourceGroupLabel)} combobox`}
                    onRenderNoMatchItem={query => ({
                        data: query,
                        key: createResourceGroupItemKey,
                        disabled: invalidResourceGroupCreation,
                        text: localize(messages.createResourceGroup, { resourceGroupName: query })
                    })}
                    onRenderLabel={() =>
                        getDescribedLabel(
                            localize(azureResourceModalsMessages.resourceGroupLabel),
                            <LuisText>
                                {localize(messages.resourceGroupDescription)}&nbsp;
                                <ExternalLink
                                    isDarkMode
                                    href="https://docs.microsoft.com/azure/azure-resource-manager/management/overview?WT.mc_id=Portal-fx#resource-groups">
                                    {localize(globalMessages.learnMore)}
                                </ExternalLink>
                            </LuisText>,
                            true
                        )
                    }
                />

                <LuisTextField
                    required
                    value={newResourceName}
                    styles={dropdownStyles}
                    errorMessage={resourceNameErrorMessage}
                    onChange={(_, name) => onResourceNameChange(name)}
                    label={localize(azureResourceModalsMessages.nameLabel)}
                    placeholder={localize(azureResourceModalsMessages.namePlaceholder)}
                />

                <LuisDropdown
                    required
                    styles={dropdownStyles}
                    selectedKey={selectedRegion}
                    isLoading={areRegionsLoading}
                    errorMessage={regionsErrorMessage}
                    disabled={!selectedSubscriptionId}
                    calloutProps={{ styles: dropdownCalloutStyles }}
                    onChange={(_, opt) => onRegionChange(opt.key as Region)}
                    label={localize(azureResourceModalsMessages.regionLabel)}
                    options={regions.map(region => ({ key: region, text: region }))}
                    placeholder={localize(azureResourceModalsMessages.regionPlaceholder)}
                />

                <Stack horizontal tokens={{ childrenGap: 15 }}>
                    <StackItem grow>
                        <LuisDropdown
                            isLoading={areSkusLoading}
                            selectedKey={selectedSkuCode}
                            errorMessage={skuErrorMessage}
                            disabled={!selectedSubscriptionId}
                            options={skus.map(s => ({ key: s.code, text: s.code }))}
                            onChange={(_, opt) => onSkuChange(opt.key as AzureSku['code'])}
                            ariaLabel={localize(azureResourceModalsMessages.pricingTierLabel)}
                            placeholder={localize(azureResourceModalsMessages.pricingTierPlaceholder)}
                            onRenderLabel={() =>
                                getDescribedLabel(
                                    localize(azureResourceModalsMessages.pricingTierLabel),
                                    localize(messages.pricingTierDescription),
                                    true
                                )
                            }
                        />
                    </StackItem>

                    <StackItem grow>
                        <Toggle
                            checked={isManagedIdentityEnabled}
                            onChange={(_, checked: boolean) => setIsManagedIdentityEnabled(checked)}
                            label={getDescribedLabel(
                                localize(azureResourceModalsMessages.managedIdentityLabel),
                                localize(azureResourceModalsMessages.managedIdentityCalloutLabel)
                            )}
                        />
                    </StackItem>
                </Stack>
            </Stack>
        </BasicModal>
    );
});
